import { useCallback, useEffect, useRef, useState } from 'react';
import { getAzureSpeechService } from '../services/azureSpeechService';
import { useTranslation } from './useTranslation';

// Azure Speech Service configuration
const SPEECH_CONFIG = {
  subscriptionKey: import.meta.env.REACT_APP_SPEECH_SUBSCRIPTION_KEY || '',
  region: import.meta.env.REACT_APP_SPEECH_REGION || 'westeurope',
  language: 'pl-PL'
};

interface SpeechRecognitionResult {
  text: string;
  confidence: number;
}

interface UseSpeechOptions {
  autoSendOnRecognition?: boolean;
  onAutoSend?: (text: string) => Promise<void>;
  onSetMessage?: (text: string) => void;
}

export const useSpeech = (options: UseSpeechOptions = {}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const { currentLanguage } = useTranslation();
  const speechServiceRef = useRef(getAzureSpeechService());
  const hasWarnedRef = useRef(false);
  const { autoSendOnRecognition = false, onAutoSend, onSetMessage } = options;

  // Initialize Azure Speech Service and update when language changes
  useEffect(() => {
    if (!SPEECH_CONFIG.subscriptionKey) {
      // Only warn once per hook instance
      if (!hasWarnedRef.current) {
        console.warn('Azure Speech Service subscription key not configured. Text-to-speech will not be available.');
        hasWarnedRef.current = true;
      }
    } else {
      // Update speech service with current language
      speechServiceRef.current = getAzureSpeechService();
    }
  }, [currentLanguage]);

  // Function to speak text using Azure Speech Service REST API
  const speakText = useCallback(async (text: string) => {
    if (!speechServiceRef.current || !ttsEnabled) {
      return;
    }

    try {
      await speechServiceRef.current.synthesizeSpeech({ text });
    } catch (error) {
      console.error('Error during speech synthesis:', error);
    }
  }, [ttsEnabled, currentLanguage]);

  // Function to speak text with streaming for long responses
  const speakTextStreaming = useCallback(async (text: string) => {
    if (!speechServiceRef.current || !ttsEnabled) {
      return;
    }

    try {
      // Use streaming TTS for better performance with long responses
      const textLength = text.length;
      const useStreaming = textLength > 100; // Use streaming for responses longer than 100 characters

      if (useStreaming) {
        // Use streaming TTS for long responses
        speechServiceRef.current.synthesizeSpeechStreaming({
          text: text,
          onChunkComplete: () => {
            // Optional: Add any chunk completion logic here
          }
        }).catch(error => {
          console.error('Error during streaming speech synthesis:', error);
        });
      } else {
        // Use regular TTS for short responses
        speechServiceRef.current.synthesizeSpeech({ text })
          .catch(error => {
            console.error('Error during speech synthesis:', error);
          });
      }
    } catch (error) {
      console.error('Error during speech synthesis:', error);
    }
  }, [ttsEnabled, currentLanguage]);

  // Function to handle microphone click for speech recognition
  const handleMicrophoneClick = useCallback(() => {
    if (!speechServiceRef.current) {
      console.error('Azure Speech Service not initialized');
      return;
    }

    if (isRecording) {
      speechServiceRef.current.stopSpeechRecognition();
      setIsRecording(false);
    } else {
      // Stop any ongoing TTS before starting speech recognition
      if (speechServiceRef.current.isSpeaking()) {
        speechServiceRef.current.stopSpeech();
      }

      speechServiceRef.current.startSpeechRecognition(
        (result: SpeechRecognitionResult) => {
          // Handle successful recognition
          setIsRecording(false);

          // Set the message text if callback provided
          if (onSetMessage) {
            onSetMessage(result.text);
          }

          // Auto-send the message if enabled
          if (autoSendOnRecognition && onAutoSend && result.text.trim()) {
            setTimeout(async () => {
              try {
                await onAutoSend(result.text);
              } catch (error) {
                console.error('Error auto-sending message:', error);
              }
            }, 500);
          }
        },
        (error: string) => {
          // Handle recognition error
          console.error('Speech recognition error:', error);
          setIsRecording(false);
        },
        () => {
          // Handle recognition end
          setIsRecording(false);
        }
      );
      setIsRecording(true);
    }
  }, [isRecording, autoSendOnRecognition, onAutoSend, currentLanguage]);

  // Toggle TTS function
  const toggleTTS = useCallback(() => {
    const newState = !ttsEnabled;
    setTtsEnabled(newState);

    // If disabling TTS, stop any ongoing speech synthesis
    if (!newState && speechServiceRef.current) {
      speechServiceRef.current.stopSpeech();
    }
  }, [ttsEnabled]);

  // Stop speech function
  const stopSpeech = useCallback(() => {
    if (speechServiceRef.current) {
      speechServiceRef.current.stopSpeech();
    }
  }, []);

  // Stop speech recognition function
  const stopSpeechRecognition = useCallback(() => {
    if (speechServiceRef.current) {
      speechServiceRef.current.stopSpeechRecognition();
      setIsRecording(false);
    }
  }, []);

  // Check if speech service is available
  const isSpeechAvailable = useCallback(() => {
    return speechServiceRef.current !== null;
  }, []);

  // Check if currently speaking
  const isSpeaking = useCallback(() => {
    return speechServiceRef.current?.isSpeaking() || false;
  }, []);

  // Check if currently recording
  const isRecordingAudio = useCallback(() => {
    return speechServiceRef.current?.isRecording() || false;
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (speechServiceRef.current) {
      speechServiceRef.current.dispose();
    }
  }, []);

  return {
    // State
    isRecording,
    ttsEnabled,

    // Functions
    speakText,
    speakTextStreaming,
    handleMicrophoneClick,
    toggleTTS,
    stopSpeech,
    stopSpeechRecognition,
    isSpeechAvailable,
    isSpeaking,
    isRecordingAudio,
    cleanup,

    // Speech service reference for direct access if needed
    speechService: speechServiceRef.current
  };
}; 
